import React, { useState, useMemo, useEffect, useRef } from 'react';
import './ForecastQuestionnaire.css';
import '../LoadingScreen/LoadingScreen.css';
import { ReactComponent as LeftArrowIcon } from '../../assets/icons/arrow-right-icon.svg';
import { ReactComponent as ChevronDownIcon } from '../../assets/icons/arrow-right-icon.svg';
import MessageFormatter from '../MessageFormatter/MessageFormatter';

const STEPS = [
  { id: 'calendar', title: 'Calendar', description: 'Set your forecast window' },
  { id: 'revenue', title: 'Revenue', description: 'Anticipated changes to sales, pricing, or customer activity' },
  { id: 'cash-inflows', title: 'Cash Inflows', description: 'Changes to collection timing or other operating receipts' },
  { id: 'cash-outflows', title: 'Cash Outflows', description: 'Changes to payment timing or other operating disbursements' },
  { id: 'financing', title: 'Financing', description: 'Planned debt, equity, or capital transactions' },
  { id: 'building', title: 'Build Forecast', description: 'Agent will complete forecast and return to you' }
];

const BUILD_PROGRESS_STEPS = [
  { key: 'Revenue',           label: 'Revenue',    buildStepId: 'revenue' },
  { key: 'Operating Inflows', label: 'Inflows',    buildStepId: 'op_inflows' },
  { key: 'Operating Outflows',label: 'Outflows',   buildStepId: 'op_outflows' },
  { key: 'Financing',         label: 'Financing',  buildStepId: 'financing' },
  { key: 'Final Review',      label: 'Review',     buildStepId: 'review' },
];

const DAYS_OF_WEEK = [
  'Monday',
  'Tuesday', 
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday'
];

const DAY_NAME_BY_INDEX = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const parseIsoDateToLocalDate = (value) => {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const [year, month, day] = value.split('-').map(Number);
  const parsed = new Date(year, month - 1, day);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

// Question configurations - focused on FUTURE KNOWN EVENTS the AI can't find in QuickBooks
const STEP_QUESTIONS = {
  revenue: {
    question: "Are there any expected changes to your revenue over the forecast period?",
    options: [
      { id: 'contracts', label: 'Significant contracts or customers starting / ending' },
      { id: 'product_changes', label: 'New product launches of discontinuations' },
      { id: 'pricing_changes', label: 'Pricing or rate changes taking effect' },
      { id: 'other_changes', label: 'Other (specify below)' },
      { id: 'no_changes', label: 'No expected changes - use historical patterns', isNoneOption: true }
    ]
  },
  'cash-inflows': {
    question: "Are there any expected changes to your cash collections or other operating receipts?",
    options: [
      { id: 'payment_timing', label: 'Significant changes to customer payment timing' },
      { id: 'large_payments', label: 'Large customer payments expected' },
      { id: 'large_receipts', label: 'Large one-time receipts (tax refunds, insurance proceeds, asset sales)' },
      { id: 'other_changes', label: 'Other (specify below)' },
      { id: 'no_changes', label: 'No expected changes - use historical patterns', isNoneOption: true }
    ]
  },
  'cash-outflows': {
    question: "Are there any expected changes to your cash payments or other operating disbursements?",
    options: [
      { id: 'headcount', label: 'Headcount changes (hiring, reductions, or freezes)' },
      { id: 'vendor_timing', label: 'Vendor payment timing changes' },
      { id: 'large_payments', label: 'Large one-time payments (equipment, legal, settlements)' },
      { id: 'capex', label: 'Capex projects starting or completing' },
      { id: 'spend_changes', label: 'Pausing or accelerating spend' },
      { id: 'other_changes', label: 'Other (specify below)' },
      { id: 'no_changes', label: 'No expected changes - use historical patterns', isNoneOption: true }
    ]
  },
  financing: {
    question: "Are there any planned financing transactions over the forecast period?",
    options: [
      { id: 'debt_draws', label: 'Debt draws or paydowns' },
      { id: 'equity_raise', label: 'Equity raises or injections' },
      { id: 'dividends', label: 'Dividends or distributions' },
      { id: 'other_changes', label: 'Other (specify below)' },
      { id: 'no_changes', label: 'No expected changes - use historical patterns', isNoneOption: true }
    ]
  }
};

function ForecastQuestionnaire({ onComplete, onBuildComplete, forecast, isBuilding = false, isFailed = false, initialData = null }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [validationError, setValidationError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentReasoningMsg, setCurrentReasoningMsg] = useState('');
  const [actionLog, setActionLog] = useState([]);
  const logRef = useRef(null);
  const isLogAtBottom = useRef(true);
  
  // Default form data structure
  const defaultFormData = {
    calendar: {
      weekStartDay: 'Monday',
      useManualStartDate: false,
      exactStartDate: ''
    },
    revenue: {
      selectedOptions: [],
      details: ''
    },
    'cash-inflows': {
      selectedOptions: [],
      details: ''
    },
    'cash-outflows': {
      selectedOptions: [],
      details: ''
    },
    financing: {
      selectedOptions: [],
      details: ''
    }
  };

  // Merge initialData with defaults if provided (auto-populate from saved user_input)
  const getInitialFormData = () => {
    if (!initialData) return defaultFormData;
    
    return {
      calendar: {
        weekStartDay: initialData.calendar?.weekStartDay || defaultFormData.calendar.weekStartDay,
        useManualStartDate: Boolean(initialData.calendar?.useManualStartDate && initialData.calendar?.exactStartDate),
        exactStartDate: initialData.calendar?.exactStartDate || ''
      },
      revenue: {
        selectedOptions: initialData.revenue?.selectedOptions || [],
        details: initialData.revenue?.details || ''
      },
      'cash-inflows': {
        selectedOptions: initialData['cash-inflows']?.selectedOptions || [],
        details: initialData['cash-inflows']?.details || ''
      },
      'cash-outflows': {
        selectedOptions: initialData['cash-outflows']?.selectedOptions || [],
        details: initialData['cash-outflows']?.details || ''
      },
      financing: {
        selectedOptions: initialData.financing?.selectedOptions || [],
        details: initialData.financing?.details || ''
      }
    };
  };

  const [formData, setFormData] = useState(getInitialFormData);

  // Update formData when initialData changes (e.g., when forecast loads after mount)
  useEffect(() => {
    if (initialData) {
      setFormData(getInitialFormData());
    }
  }, [initialData]);

  // Reset isSubmitting when forecast status updates to in-progress (backend confirmation)
  useEffect(() => {
    if (isSubmitting && forecast?.status === 'in-progress') {
      setIsSubmitting(false);
    }
  }, [forecast?.status, isSubmitting]);

  // SSE: Subscribe to reasoning stream while the forecast is building.
  // Closing this connection (navigate away, reload) does NOT affect the backend build.
  useEffect(() => {
    const shouldSubscribe =
      forecast?.id &&
      (isBuilding || isSubmitting || forecast?.status === 'in-progress');

    if (!shouldSubscribe) return;

    setCurrentReasoningMsg('');
    setActionLog([]);

    const url = `${process.env.REACT_APP_API_BASE_URL}/api/forecasts/reasoning-stream/${forecast.id}`;
    const es = new EventSource(url, { withCredentials: true });
    const LOG_EVENT_TYPES = new Set(['phase', 'progress', 'reasoning', 'thinking', 'agent_text']);

    es.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'done') {
          es.close();
          if (onBuildComplete) {
            onBuildComplete({ success: data.success, forecastId: forecast.id });
          }
          return;
        }
        const TILE_TYPES = ['phase', 'progress', 'reasoning'];
        if (TILE_TYPES.includes(data.type) && data.message) {
          setCurrentReasoningMsg(data.message);
        }
        if (LOG_EVENT_TYPES.has(data.type)) {
          setActionLog(prev => [...prev, { ...data, _ts: Date.now() }]);
        }
      } catch {
        // ignore non-JSON
      }
    };

    es.onerror = () => {
      es.close();
    };

    return () => {
      es.close();
    };
  }, [forecast?.id, isBuilding, isSubmitting, forecast?.status]);

  const handleLogScroll = () => {
    const el = logRef.current;
    if (!el) return;
    const threshold = 40;
    isLogAtBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight <= threshold;
  };

  useEffect(() => {
    const el = logRef.current;
    if (el && isLogAtBottom.current) el.scrollTop = el.scrollHeight;
  }, [actionLog.length]);

  const completedStepIdx = useMemo(() => {
    // Seed from persisted build_steps (survives page refresh)
    let maxIdx = -1;
    const steps = forecast?.build_steps;
    if (Array.isArray(steps)) {
      for (const step of steps) {
        if (step.status === 'completed') {
          const idx = BUILD_PROGRESS_STEPS.findIndex(s => s.buildStepId === step.id);
          if (idx > maxIdx) maxIdx = idx;
        }
      }
    }
    // Layer on SSE events from the current session
    for (const entry of actionLog) {
      if (entry.toolName === 'complete_step' && entry.detail) {
        const idx = BUILD_PROGRESS_STEPS.findIndex(s => s.key === entry.detail);
        if (idx > maxIdx) maxIdx = idx;
      }
    }
    return maxIdx;
  }, [actionLog, forecast?.build_steps]);

  // If forecast is in-progress, failed, or submitting, show the building step
  const effectiveStep = isBuilding || isFailed || forecast?.status === 'in-progress' || forecast?.status === 'failed' || isSubmitting ? STEPS.length - 1 : currentStep;
  const isBuildingStep = effectiveStep === STEPS.length - 1;
  const isFailedState = !isSubmitting && (isFailed || forecast?.status === 'failed');

  const handleRetry = () => {
    if (onComplete) {
      setIsSubmitting(true);
      onComplete(formData, { isRetry: true });
    }
  };

  const handleNext = () => {
    const stepId = STEPS[currentStep]?.id;
    const isQuestionStepId = ['revenue', 'cash-inflows', 'cash-outflows', 'financing'].includes(stepId);

    if (stepId === 'calendar') {
      const calendarError = getManualStartDateError();
      if (calendarError) {
        setValidationError(calendarError);
        return;
      }
    }
    
    // Validate question steps: must select at least one option
    if (isQuestionStepId) {
      const selectedOptions = formData[stepId]?.selectedOptions || [];
      if (selectedOptions.length === 0) {
        setValidationError('Please select at least one option');
        return;
      }
      
      // If non-none options are selected, details are required
      if (hasNonNoneOptionsSelected(stepId)) {
        const details = formData[stepId]?.details || '';
        if (!details.trim()) {
          setValidationError('Please provide details for your selections');
          return;
        }
      }
    }
    
    setValidationError('');
    
    if (currentStep < STEPS.length - 2) {
      setCurrentStep(currentStep + 1);
    } else if (currentStep === STEPS.length - 2) {
      handleFinish();
    }
  };

  const handleBack = () => {
    setValidationError('');
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleFinish = () => {
    if (onComplete) {
      setIsSubmitting(true);
      onComplete(formData);
    }
  };

  const updateFormData = (stepId, data) => {
    setFormData(prev => ({
      ...prev,
      [stepId]: { ...prev[stepId], ...data }
    }));
  };

  // Toggle checkbox option
  const toggleOption = (stepId, optionId, isNoneOption) => {
    const currentOptions = formData[stepId]?.selectedOptions || [];
    const stepConfig = STEP_QUESTIONS[stepId];
    
    if (isNoneOption) {
      // If clicking "none" option, clear all others and select only this one
      if (currentOptions.includes(optionId)) {
        updateFormData(stepId, { selectedOptions: [], details: '' });
      } else {
        updateFormData(stepId, { selectedOptions: [optionId], details: '' });
      }
    } else {
      // If clicking a regular option, remove any "none" option
      const noneOption = stepConfig?.options.find(o => o.isNoneOption);
      let newOptions = currentOptions.filter(id => id !== noneOption?.id);
      
      if (newOptions.includes(optionId)) {
        newOptions = newOptions.filter(id => id !== optionId);
      } else {
        newOptions = [...newOptions, optionId];
      }
      updateFormData(stepId, { selectedOptions: newOptions });
    }
  };

  // Check if details are required (has non-none options selected)
  const hasNonNoneOptionsSelected = (stepId) => {
    const currentOptions = formData[stepId]?.selectedOptions || [];
    const stepConfig = STEP_QUESTIONS[stepId];
    if (!stepConfig) return false;
    
    const noneOption = stepConfig.options.find(o => o.isNoneOption);
    return currentOptions.length > 0 && !currentOptions.includes(noneOption?.id);
  };

  // Calculate forecast start date based on selected week start day
  const getManualStartDateError = () => {
    const { useManualStartDate, exactStartDate, weekStartDay } = formData.calendar;

    if (!useManualStartDate) return '';
    if (!exactStartDate) return 'Please enter an exact start date';

    const parsedDate = parseIsoDateToLocalDate(exactStartDate);
    if (!parsedDate) return 'Please enter a valid start date';

    const selectedDay = weekStartDay || 'Monday';
    const actualDay = DAY_NAME_BY_INDEX[parsedDate.getDay()];
    if (actualDay !== selectedDay) {
      return `Exact start date must fall on ${selectedDay}`;
    }

    return '';
  };

  const forecastStartDate = useMemo(() => {
    const manualDate = parseIsoDateToLocalDate(formData.calendar.exactStartDate);
    if (formData.calendar.useManualStartDate && manualDate) {
      return manualDate;
    }

    const selectedDay = formData.calendar.weekStartDay || 'Monday';
    const today = new Date();
    const currentDayIndex = today.getDay();
    const targetDayIndex = DAYS_OF_WEEK.indexOf(selectedDay) + 1;
    const adjustedTargetIndex = targetDayIndex === 7 ? 0 : targetDayIndex;
    
    let daysToGoBack = currentDayIndex - adjustedTargetIndex;
    if (daysToGoBack < 0) {
      daysToGoBack += 7;
    }
    
    const startDate = new Date(today);
    startDate.setDate(today.getDate() - daysToGoBack);
    return startDate;
  }, [formData.calendar.weekStartDay, formData.calendar.useManualStartDate, formData.calendar.exactStartDate]);

  const forecastEndDate = useMemo(() => {
    const endDate = new Date(forecastStartDate);
    endDate.setDate(forecastStartDate.getDate() + (13 * 7) - 1);
    return endDate;
  }, [forecastStartDate]);

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const currentStepData = STEPS[effectiveStep];
  const isFirstStep = effectiveStep === 0;
  const isLastQuestionStep = effectiveStep === STEPS.length - 2;
  const manualStartDateError = currentStepData?.id === 'calendar' ? getManualStartDateError() : '';

  const getStepState = (stepIndex) => {
    if (stepIndex < effectiveStep) return 'completed';
    if (stepIndex === effectiveStep) return 'current';
    return 'pending';
  };

  // Render the options list for a question step (scrollable area)
  const renderQuestionOptions = (stepId) => {
    const config = STEP_QUESTIONS[stepId];
    if (!config) return null;

    const selectedOptions = formData[stepId]?.selectedOptions || [];
    const showOptionsError = validationError === 'Please select at least one option' && selectedOptions.length === 0;

    return (
      <div className="step-content question-step">
        <p className="step-question">{config.question}</p>
        
        <div className="options-scroll">
          <div className={`options-list ${showOptionsError ? 'has-error' : ''}`}>
            {config.options.map(option => {
              const isChecked = selectedOptions.includes(option.id);
              return (
                <label 
                  key={option.id} 
                  className={`option-item ${isChecked ? 'checked' : ''} ${option.isNoneOption ? 'none-option' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => {
                      setValidationError('');
                      toggleOption(stepId, option.id, option.isNoneOption);
                    }}
                  />
                  <span className="option-label">{option.label}</span>
                </label>
              );
            })}
          </div>
          {showOptionsError && (
            <span className="error-message options-error">{validationError}</span>
          )}
        </div>
      </div>
    );
  };

  // Render the details input (static at bottom)
  const renderDetailsInput = (stepId) => {
    const details = formData[stepId]?.details || '';
    const isRequired = hasNonNoneOptionsSelected(stepId);
    const showError = validationError === 'Please provide details for your selections' && isRequired && !details.trim();

    return (
      <div className="details-section">
        <label className="details-label">
          Provide any details {isRequired ? (
            <span className="required">(required)</span>
          ) : (
            <span className="optional">(optional)</span>
          )}
        </label>
        <textarea
          className={`details-textarea ${showError ? 'error' : ''}`}
          placeholder="Provide any additional detail (includes dates when applicable)"
          value={details}
          onChange={(e) => {
            setValidationError('');
            updateFormData(stepId, { details: e.target.value });
          }}
          rows={2}
        />
        {showError && (
          <span className="error-message">{validationError}</span>
        )}
      </div>
    );
  };

  // Check if current step is a question step (not calendar or building)
  const isQuestionStep = ['revenue', 'cash-inflows', 'cash-outflows', 'financing'].includes(currentStepData?.id);

  return (
    <div className="forecast-questionnaire">
      {/* Left: Question Content */}
      <div className="questionnaire-main">
        {isBuildingStep ? (
          isFailedState ? (
            <div className="building-state failed-state">
              <div className="failed-icon">!</div>
              <h2 className="building-title">Forecast Build Failed</h2>
              <p className="building-description">Something went wrong while building your forecast</p>
              <button className="btn-retry" onClick={handleRetry}>
                Retry Build
              </button>
            </div>
          ) : (
            <div className="building-state">
              <div className="building-top">
                <div className="building-header">
                  <div className="loading-squares">
                    {[...Array(4)].map((_, i) => (
                      <div key={i} className={`square square${i + 1}`} />
                    ))}
                  </div>
                  <h2 className="building-title">Forecast is being built...</h2>
                  <p className="building-description">This may take a few minutes</p>
                </div>

                {(actionLog.length > 0 || completedStepIdx >= 0) && (
                  <div className="build-progress-bar">
                    <div className="progress-track" />
                    <div
                      className="progress-track-fill"
                      style={{ width: `${(Math.min(completedStepIdx + 1, BUILD_PROGRESS_STEPS.length - 1) / (BUILD_PROGRESS_STEPS.length - 1)) * 80}%` }}
                    />
                    {BUILD_PROGRESS_STEPS.map((step, i) => {
                      const done = i <= completedStepIdx;
                      const active = i === completedStepIdx + 1 && completedStepIdx < BUILD_PROGRESS_STEPS.length - 1;
                      return (
                        <div key={step.key} className={`progress-stop ${done ? 'done' : ''} ${active ? 'active' : ''}`}>
                          <div className="progress-stop-dot">
                            {done && (
                              <svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M3 6.5L5 8.5L9 3.5" stroke="white" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            )}
                          </div>
                          <span className="progress-stop-label">{step.label}</span>
                        </div>
                      );
                    })}
                  </div>
                )}

                {currentReasoningMsg && (
                  <div className="reasoning-card" key={currentReasoningMsg}>
                    <span className="reasoning-live-dot" />
                    <p className="reasoning-status">{currentReasoningMsg}</p>
                  </div>
                )}
              </div>
            </div>
          )
        ) : (
          <div className="questionnaire-content-wrapper">
            <div className="questionnaire-header">
              <div className="step-number">{effectiveStep + 1}</div>
              <h1 className="step-title">{currentStepData.title}</h1>
            </div>

            <div className="questionnaire-content">
              {currentStepData.id === 'calendar' && (
                <div className="step-content">
                  <div className="calendar-config">
                    <div className="config-section">
                      <div className="subtitle-label">Choose the starting day of the week for your forecast. The calendar dates will adjust automatically based on your selection.</div>
                      <label className="config-label">Week Start Day</label>
                      <div className="dropdown-container">
                        <select 
                          className="day-dropdown"
                          value={formData.calendar.weekStartDay}
                          onChange={(e) => updateFormData('calendar', { weekStartDay: e.target.value })}
                        >
                          {DAYS_OF_WEEK.map(day => (
                            <option key={day} value={day}>{day}</option>
                          ))}
                        </select>
                        <ChevronDownIcon className="dropdown-icon" />
                      </div>
                    </div>

                    <div className="forecast-preview">
                      <h3 className="preview-title">Forecast Preview</h3>
                      <div className="preview-grid">
                        <div className="preview-item">
                          <span className="preview-label">Frequency</span>
                          <span className="preview-value">Weekly</span>
                        </div>
                        <div className="preview-item">
                          <span className="preview-label">Duration</span>
                          <span className="preview-value">13 Weeks</span>
                        </div>
                        <div className="preview-item">
                          <span className="preview-label">Start Date</span>
                          <span className="preview-value">{formatDate(forecastStartDate)}</span>
                        </div>
                        <div className="preview-item">
                          <span className="preview-label">End Date</span>
                          <span className="preview-value">{formatDate(forecastEndDate)}</span>
                        </div>
                      </div>
                    </div>
                    <div className="config-section advanced-config-section">
                      <button
                        type="button"
                        className={`advanced-toggle ${formData.calendar.useManualStartDate ? 'open' : ''}`}
                        onClick={() => {
                          setValidationError('');
                          updateFormData('calendar', {
                            useManualStartDate: !formData.calendar.useManualStartDate
                          });
                        }}
                      >
                        <span>Advanced</span>
                        <ChevronDownIcon className="advanced-toggle-icon" />
                      </button>

                      {formData.calendar.useManualStartDate && (
                        <div className="advanced-panel">
                          <label className="config-label" htmlFor="exact-start-date">Exact Start Date</label>
                          <input
                            id="exact-start-date"
                            type="date"
                            className={`exact-date-input ${manualStartDateError ? 'error' : ''}`}
                            value={formData.calendar.exactStartDate || ''}
                            onChange={(e) => {
                              setValidationError('');
                              updateFormData('calendar', { exactStartDate: e.target.value });
                            }}
                          />
                          <span className="advanced-help-text">
                            Use this to start the forecast on a specific date.
                          </span>
                          {manualStartDateError && (
                            <span className="error-message">{manualStartDateError}</span>
                          )}
                        </div>
                      )}
                    </div>

                  </div>
                </div>
              )}

              {currentStepData.id === 'revenue' && renderQuestionOptions('revenue')}
              {currentStepData.id === 'cash-inflows' && renderQuestionOptions('cash-inflows')}
              {currentStepData.id === 'cash-outflows' && renderQuestionOptions('cash-outflows')}
              {currentStepData.id === 'financing' && renderQuestionOptions('financing')}
            </div>

            {/* Details input - static at bottom for question steps */}
            {isQuestionStep && renderDetailsInput(currentStepData.id)}

            <div className="questionnaire-footer">
              <button 
                className="btn-back" 
                onClick={handleBack}
                disabled={isFirstStep}
                style={{ visibility: isFirstStep ? 'hidden' : 'visible' }}
              >
                <LeftArrowIcon className="back-icon" />
                Back
              </button>
              <button 
                className="btn-next" 
                onClick={handleNext}
              >
                {isLastQuestionStep ? 'Build with AI' : 'Next'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Right: Progress sidebar OR Build activity log */}
      <div className="questionnaire-progress">
        {isBuildingStep && !isFailedState ? (
          <>
            <div className="progress-header">
              <span className="progress-header-title">Build Progress</span>
            </div>
            <div className="build-action-log" ref={logRef} onScroll={handleLogScroll}>
              {actionLog.map((entry, i) => {
                const isLatest = i === actionLog.length - 1;
                return (
                  <div key={i} className={`log-entry ${isLatest && entry.message && entry.detail ? 'log-latest' : ''}`}>
                    <div className="log-icon-col">
                      {isLatest ? (
                        <span className="log-dot log-dot-active" />
                      ) : entry.type === 'progress' ? (
                        <svg className="log-check-svg" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M3.5 7.5L6 10L10.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      ) : (
                        <span className="log-dot log-dot-past" />
                      )}
                    </div>
                    <div className="log-content">
                      {entry.type === 'thinking' ? (
                        <span className="log-text log-text-thinking">
                          Thinking{isLatest
                            ? <span className="thinking-dots"><span>.</span><span>.</span><span>.</span></span>
                            : '...'}
                        </span>
                      ) : entry.type === 'reasoning' ? (
                        <>
                          <span className="log-text log-text-reasoning">{entry.message}</span>
                          {entry.detail && <span className="log-detail">{entry.detail}</span>}
                        </>
                      ) : entry.type === 'agent_text' ? (
                        <div className="log-agent-markdown message-bubble">
                          <MessageFormatter message={entry.message} />
                        </div>
                      ) : entry.toolName === 'complete_step' ? (
                        <>
                          <span className="log-text log-text-validate">{entry.message}</span>
                          {entry.detail && <span className="log-detail log-detail-validate">{entry.detail}</span>}
                        </>
                      ) : (
                        <>
                          <span className="log-text">{entry.message}</span>
                          {(entry.toolName === 'execute_sql' || entry.toolName === 'run_script') && entry.detail ? (
                            <pre className={`log-code-block ${entry.toolName === 'run_script' ? 'log-code-python' : 'log-code-sql'}`}>
                              <code>{entry.detail.split('\n').slice(0, 3).join('\n')}{entry.detail.split('\n').length > 3 ? '\n…' : ''}</code>
                            </pre>
                          ) : entry.detail ? (
                            <span className="log-detail">{entry.detail}</span>
                          ) : null}
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <>
            <div className="progress-header">
              <span className="progress-header-title">Build Progress</span>
            </div>
            <div className="progress-list">
              {STEPS.map((step, index) => {
                const stepState = getStepState(index);
                return (
                  <div 
                    className={`progress-item ${stepState}`} 
                    key={step.id}
                  >
                    <div className="progress-indicator">
                      {index > 0 && <div className={`progress-line ${stepState} ${isFailedState && step.id === 'building' ? 'failed' : ''}`} />}
                      <div className={`progress-circle ${stepState} ${isFailedState && step.id === 'building' && stepState === 'current' ? 'failed' : ''}`}>
                        {stepState === 'current' && step.id === 'building' ? (
                          isFailedState ? (
                            <div className="progress-failed-icon">!</div>
                          ) : (
                            <div className="progress-spinner" />
                          )
                        ) : (
                          <div className="progress-dot" />
                        )}
                      </div>
                    </div>
                    <div className="progress-content">
                      <div className="progress-title">{step.title}</div>
                      {step.description && (
                        <div className="progress-subtitle">{step.description}</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default ForecastQuestionnaire;
